import { useState } from 'react';
import {
  Plus, Search, MoreHorizontal, Eye, MessageSquare, Phone,
  ArrowUpToLine, Edit3, Trash2, Clock, Wallet, ChevronRight,
  AlertCircle, RefreshCw, Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuthStore } from '@/store/authStore';
import { getInitials } from '@/utils';
import { Separator } from '@/components/ui/misc';
import { Info, ShoppingCart as CartIcon, ChevronUp, X as CloseIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatNumber } from '@/utils';

const TABS = [
  { id: 'active', label: 'ĐANG HIỂN THỊ', count: 2 },
  { id: 'expired', label: 'HẾT HẠN', count: 0 },
  { id: 'rejected', label: 'BỊ TỪ CHỐI', count: 1 },
  { id: 'payment', label: 'CẦN THANH TOÁN', count: 0 },
  { id: 'draft', label: 'TIN NHÁP', count: 0 },
  { id: 'pending', label: 'CHỜ DUYỆT', count: 0 },
];

const MOCK_POSTS = [
  {
    id: 1,
    title: 'Nệm Gummi Lite 160x200x10cm',
    price: '2.500.000 đ',
    address: 'Phường Phú Trung, Quận Tân Phú, Tp Hồ Chí Minh',
    status: 'active',
    views: 48,
    rank: 106,
    category: 'Giường, chăn ga gối nệm',
    createdAt: '21/03/26',
    expiredAt: '20:45 20/05/26',
    image: 'https://picsum.photos/seed/nem/400/300',
    chartData: [20, 35, 25, 48, 30, 42, 28], // Mock points for a line chart
  },
];

export default function UserManagePostsPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('active');
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>(['regular']);
  const [duration, setDuration] = useState(1); // 1, 3, 7 days
  const [targetPost, setTargetPost] = useState<any>(null);

  const calculateTotal = () => {
    let total = 0;
    if (selectedServices.includes('combo')) total += 8000;
    if (selectedServices.includes('regular')) {
      if (duration === 1) total += 5000;
      if (duration === 3) total += 14000;
      if (duration === 7) total += 32000;
    }
    if (selectedServices.includes('scheduled')) total += 5000;
    return total;
  };

  const handlePushClick = (post: any) => {
    setTargetPost(post);
    setPushModalOpen(true);
  };

  return (
    <>
    <div className="min-h-screen bg-[#f8f9fa] py-8 animate-fade-in">
      <div className="max-w-[1000px] mx-auto px-4">

        {/* User Profile Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border-2 border-orange-100">
              <AvatarFallback className="bg-orange-500 text-white text-xl font-bold font-display">
                {getInitials(user?.fullName || user?.username || 'U')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{user?.fullName || user?.username || 'Người dùng'}</h2>
              <button className="text-sm text-blue-600 hover:underline flex items-center mt-1">
                <Plus size={14} className="mr-1" /> Tạo cửa hàng
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-full border border-gray-100">
            <Wallet size={16} className="text-orange-500" />
            <span className="text-sm font-medium text-gray-600">Số dư: <span className="font-bold text-gray-900">0</span></span>
            <button className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white hover:bg-green-600 transition-colors">
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
          <div className="flex items-center border-b overflow-x-auto no-scrollbar">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-6 py-4 text-[12px] font-bold whitespace-nowrap border-b-2 transition-all flex items-center gap-1.5',
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Post Items */}
        <div className="space-y-6">
          {MOCK_POSTS.map((post) => (
            <div key={post.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left Column: Image & Basic Info */}
                  <div className="flex-1">
                    <div className="flex gap-6">
                      <div className="w-32 h-32 rounded-lg overflow-hidden shrink-0 border border-gray-100">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[17px] font-bold text-gray-900 mb-1">{post.title}</h3>
                        <p className="text-red-500 font-bold text-lg">{post.price}</p>
                        <p className="text-sm text-gray-500 mt-2">{post.address}</p>
                        <div className="flex flex-col gap-1 mt-2 text-xs text-gray-400">
                          <span>Ngày đăng tin: {post.createdAt}</span>
                          <span>Ngày hết hạn: {post.expiredAt}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Actions for Post */}
                    <div className="flex items-center gap-3 mt-8">
                      <button className="flex-1 h-10 flex items-center justify-center gap-2 border border-gray-200 rounded-lg text-[13px] font-bold text-gray-700 hover:bg-gray-50 transition-colors">
                        <RefreshCw size={14} /> Gia hạn tin
                      </button>
                      <button className="flex-1 h-10 flex items-center justify-center gap-2 border border-gray-200 rounded-lg text-[13px] font-bold text-gray-700 hover:bg-gray-50 transition-colors">
                        <Edit3 size={14} /> Sửa tin
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">
                        <MoreHorizontal size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Right Column: Analytics & Services */}
                  <div className="lg:w-[380px] space-y-4">
                    {/* Tiny Chart / Rank Box */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          Lượt xem <AlertCircle size={12} />
                        </div>
                        <div className="text-[11px] font-bold text-gray-700 uppercase tracking-tight">
                          TRANG {post.rank}: Mục {post.category} <AlertCircle size={10} className="inline ml-1" />
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-orange-600 font-bold">
                          <AlertCircle size={16} />
                          <span className="text-xl">{post.views}</span>
                        </div>
                        {/* Simple SVG Chart */}
                        <div className="flex-1 h-10 flex items-end">
                          <div className="w-full bg-gray-200 h-[2px] relative rounded-full overflow-hidden">
                            <div className="absolute left-0 top-0 h-full bg-green-500 w-[60%]" />
                            <div className="absolute left-[60%] top-[-4px] w-2.5 h-2.5 bg-white border-2 border-green-500 rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Service Box */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">Dịch vụ gần đây</span>
                        <button className="text-[12px] font-bold text-blue-600 hover:underline">Xem chi tiết</button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <AlertCircle size={14} />
                        <span>Chưa sử dụng dịch vụ nào</span>
                      </div>
                    </div>

                    {/* Main CTA Buttons */}
                    <div className="flex gap-3 mt-4">
                      <button 
                        onClick={() => handlePushClick(post)}
                        className="flex-[1.5] h-11 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-all shadow-sm active:scale-95"
                      >
                        <ArrowUpToLine size={16} fill="currentColor" /> Đẩy tin
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        </div>

      </div>

      {/* ── SERVICE SELECTION MODAL (CUSTOM IMPLEMENTATION) ── */}
      {pushModalOpen && createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in" onClick={() => setPushModalOpen(false)} />
          <div className="relative bg-white w-full max-w-[700px] rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Chọn dịch vụ cho tin đăng</h2>
              <button onClick={() => setPushModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors">
                <CloseIcon size={20} />
              </button>
            </div>

            <div className="p-8 max-h-[70vh] overflow-y-auto no-scrollbar">
              {/* Combo Section */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Combo ưu đãi</h3>
                <p className="text-xs text-gray-400 mb-4">Đa dạng mốc thời gian và loại hình sử dụng, giúp bạn tìm ra lựa chọn phù hợp</p>
                
                <div className={cn(
                  "relative flex items-center gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer",
                  selectedServices.includes('combo') ? "border-green-500 bg-green-50/20" : "border-gray-100 bg-white"
                )} onClick={() => setSelectedServices(prev => prev.includes('combo') ? prev.filter(s => s !== 'combo') : [...prev, 'combo'])}>
                  <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center bg-white shrink-0">
                     {selectedServices.includes('combo') && <div className="w-3 h-3 bg-green-500 rounded-sm" />}
                  </div>
                  <div className="w-14 h-14 bg-gray-100 rounded-lg shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">Tin nổi bật + Đẩy tin</span>
                      <Info size={14} className="text-gray-300" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Bao gồm Tin nổi bật - Nhiều hình ảnh và Đẩy tin tự động mỗi ngày</p>
                    <p className="text-sm font-bold text-blue-600 mt-2">8.000 đ</p>
                  </div>
                </div>
              </div>

              {/* Push Section */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Đẩy tin</h3>
                <p className="text-xs text-gray-400 mb-4">Đẩy tin đăng lên đầu trang tìm kiếm như một tin mới để tăng khả năng tiếp cận</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Regular Push */}
                  <div className={cn(
                    "flex flex-col rounded-2xl border-2 transition-all overflow-hidden",
                    selectedServices.includes('regular') ? "border-green-500 bg-green-50/20" : "border-gray-100 bg-white"
                  )}>
                    <div className="p-5 flex items-center gap-4 cursor-pointer" onClick={() => setSelectedServices(prev => prev.includes('regular') ? prev.filter(s => s !== 'regular') : [...prev, 'regular'])}>
                      <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center bg-white shrink-0">
                         {selectedServices.includes('regular') && <div className="w-3 h-3 bg-green-500 rounded-sm" />}
                      </div>
                      <div className="w-14 h-14 bg-gray-100 rounded-lg shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 truncate">Đẩy tin thường</span>
                          <Info size={14} className="text-gray-300" />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">Đẩy tin ngay 1 lần hoặc lặp lại mỗi ngày, bắt đầu từ thời điểm thanh toán</p>
                        <p className="text-sm font-bold text-blue-600 mt-2">5.000 đ</p>
                      </div>
                    </div>
                    
                    {/* Duration Selector */}
                    <div className="px-5 pb-5 grid grid-cols-3 gap-2">
                      {[1, 3, 7].map(d => (
                        <button 
                          key={d}
                          onClick={() => setDuration(d)}
                          className={cn(
                            "py-2 rounded-lg border text-[11px] font-bold flex flex-col items-center justify-center transition-all",
                            duration === d ? "border-green-500 bg-green-50 text-green-700" : "border-gray-100 text-gray-500 hover:bg-gray-50"
                          )}
                        >
                          <span>{d} Ngày</span>
                          <span className="opacity-70">{d === 1 ? '5.000 đ' : d === 3 ? '14.000 đ' : '32.000 đ'}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Scheduled Push */}
                  <div className={cn(
                    "flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer h-full",
                    selectedServices.includes('scheduled') ? "border-green-500 bg-green-50/20" : "border-gray-100 bg-white"
                  )} onClick={() => setSelectedServices(prev => prev.includes('scheduled') ? prev.filter(s => s !== 'scheduled') : [...prev, 'scheduled'])}>
                    <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center bg-white shrink-0">
                       {selectedServices.includes('scheduled') && <div className="w-3 h-3 bg-green-500 rounded-sm" />}
                    </div>
                    <div className="w-14 h-14 bg-gray-100 rounded-lg shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">Đẩy tin hẹn giờ</span>
                        <Info size={14} className="text-gray-300" />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">Tiện lợi và chủ động chọn khung giờ đẩy tin hiệu quả</p>
                      <p className="text-sm font-bold text-blue-600 mt-2">5.000 đ</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Checkout Footer */}
            <div className="bg-white border-t p-4 flex items-center justify-between px-8 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
              <button className="flex items-center gap-2 text-[13px] font-bold text-gray-700 hover:bg-gray-50 p-2 rounded-lg transition-colors">
                <CartIcon size={18} />
                <span>{selectedServices.length} dịch vụ</span>
                <ChevronUp size={16} />
              </button>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 font-medium">Tổng tiền</p>
                  <p className="text-lg font-bold text-gray-900">{formatNumber(calculateTotal())} đ</p>
                </div>
                <button className="bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-3 rounded-xl transition-all shadow-lg shadow-green-100 active:scale-95">
                  Thanh toán ({selectedServices.length})
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
